import { useEffect, useRef, useState } from "react";
import { startTestTone, type TestToneHandle } from "../../audio/testTone";
import {
  countSelectableOutputs,
  supportsOutputSelection,
  type AudioDevice,
} from "../../audio/deviceEnumerator";
import { TTS_PHRASES } from "../../audio/ttsPhrases";
import { DeviceSelect } from "./DeviceSelect";
import type { PreflightVoice } from "../../hooks/usePreflightVoice";

// スピーカー確認 (spec 2026-07-26 D25)。テスト音を鳴らし、ボタンの応答だけを待つ。
// **この step では声を聴取しない** (D25-1): ここはマイクが音を拾えるかを検証する前の
// 段階であり、拾えない人が案内どおり「はい」と言っても何も起きないまま出られなくなる。
// マイクの検証は次の氏名 step が兼ねる (そこは 3 回失敗でキーボード入力に倒れる)。
// 音が届かない人にはこの案内自体も届かないため、画面テキストと出力先セレクトが唯一の
// 受け皿になる (前提 spec 2026-07-19 §4.2 の受容事項)。
// 読み上げ文言は ttsPhrases.ts に集約 (事前生成の単一ソース化、2026-07-26)。
//
// 「聞こえない」の申告は段階開示にする (D26-1)。押すと対処モードへ入り、頻度順の
// チェックリストと強調したプルダウンが出る。押しても画面が変わらない状態
// (旧「聞こえません」は「🔊 もう一度再生」と同じ handleReplay を呼ぶだけだった) が
// この spec の出発点。モードは一方通行 (D26-2) で、「聞こえました」は常に押せる。

// テスト音の鳴らし方 (D20 + D25-2)。初回は 2 回続けて再生し、以降は約 10 秒ごとに
// 1 回ずつ鳴らし直す。ただし自動での鳴らし直しは MAX_REPROMPTS 回で打ち止めにする —
// 応答手段がボタンだけになった以上、押されるまで鳴らし続けると「鳴りっぱなしが
// うるさい」(D20 の実機フィードバック) に逆戻りするため。打ち止め後はボタン待ち。
const INITIAL_PLAYS = 2;
const REPROMPT_PLAYS = 1;
const REPROMPT_SILENCE_MS = 10000;
const MAX_REPROMPTS = 3;

// 対処モードの手順②。出力先を選び直せるかどうかで助言が変わる (D26-5)。
// 選べないのに「選び直してください」と書くと実行不能な指示になる。件数は
// countSelectableOutputs を通した後の値を渡すこと (生の length には Chrome の
// 合成エントリ「既定」「通信用」が混ざる)。
function outputAdvice(canSelect: boolean, selectableCount: number): { title: string; sub: string } {
  if (!canSelect) {
    return {
      title: "端末のサウンド設定で出力先を選び直す",
      sub: "Bluetooth のイヤホンは、接続が切れていることがあります",
    };
  }
  if (selectableCount <= 1) {
    return {
      title: "端末のサウンド設定で出力先を確認する",
      sub: "Bluetooth のイヤホンは、つなぎ直すと下の「スピーカー」に表示されます",
    };
  }
  return {
    title: "下の「スピーカー」から出力先を選び直す",
    sub: "Bluetooth のイヤホンは、接続が切れていることがあります",
  };
}

export type SpeakerStats = { playCycles: number; replays: number };

type Props = {
  voice: PreflightVoice;
  devices: AudioDevice[];
  outputDeviceId: string | null;
  onSelectOutput: (deviceId: string | null) => void;
  // playCycles = ボタンを押すまでに鳴らしたサイクル数、replays = 本人の操作で
  // 鳴らし直した回数 (D26-6)。playCycles = 1 + 自動 reprompt + replays が常に成り立つ。
  onHeard: (method: "button", stats: SpeakerStats) => void;
  // 対処モードに入った (= 聞こえないと申告した) 直後に 1 回だけ呼ばれる (D26-6)。
  onUnheard: (stats: SpeakerStats) => void;
};

export function SpeakerCheck({
  voice,
  devices,
  outputDeviceId,
  onSelectOutput,
  onHeard,
  onUnheard,
}: Props) {
  const [mode, setMode] = useState<"testing" | "troubleshooting">("testing");
  const [toneExhausted, setToneExhausted] = useState(false);
  const toneRef = useRef<TestToneHandle | null>(null);
  const cyclesRef = useRef(0);
  // 本人の操作で鳴らし直した回数 (D26-6)。3 つの再生ボタンすべてがここを 1 増やす。
  const replaysRef = useRef(0);
  // 自動で鳴らし直した回数。MAX_REPROMPTS で打ち止め、ボタン操作で 0 に戻す (D25-3)。
  const repromptsRef = useRef(0);
  const doneRef = useRef(false);
  // 単一所有タイマー (clear-then-set)。予約が二重に張られると cadence が乱れるため、
  // 張る前に必ず前の予約を捨てる。
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // サイクルの世代カウンタ (レビュー修正 1)。runCycle を呼ぶたびに 1 つ進め、その値を
  // announce の非同期処理全体に持ち歩く。announce 完了時 / タイマー発火時に
  // 「自分が今も最新世代か」を確認し、古ければ何もせず降りる — 追い越しガード。
  // **注意: doneRef/cyclesRef/repromptsRef と違い、effect 内で 0 に reset してはならない。**
  // StrictMode の mount→cleanup→remount は同一 fiber の同一 ref を再利用するため、
  // remount 側が doneRef を false に戻すと、cleanup 済みのはずの 1 回目の chain が
  // 「doneRef だけ」を見ていると復活してしまう (実際に旧実装はこの穴を持っていた)。
  // このカウンタは effect の実行をまたいで単調増加させることで、doneRef が reset された
  // 後でも「自分が今の世代か」だけで古い chain を判別できるようにする。
  const cycleGenRef = useRef(0);
  // effect の依存に入れず ref 経由で最新値を読む: 親の再レンダーで cadence が
  // 再起動しないようにする。
  const onHeardRef = useRef(onHeard);
  onHeardRef.current = onHeard;
  const onUnheardRef = useRef(onUnheard);
  onUnheardRef.current = onUnheard;
  const outputDeviceIdRef = useRef(outputDeviceId);
  outputDeviceIdRef.current = outputDeviceId;

  const stopTone = () => {
    toneRef.current?.stop();
    toneRef.current = null;
  };

  const clearTimer = () => {
    if (timerRef.current !== null) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  };

  // テスト音を count 回だけ再生する。完了済み、または自分より新しい世代の runCycle に
  // 追い越されていれば途中で止める (レビュー修正 1)。合成・再生に失敗したら固定トーンへ
  // 倒す (D4)。
  const announce = async (count: number, gen: number) => {
    for (let i = 0; i < count; i++) {
      if (doneRef.current || gen !== cycleGenRef.current) return;
      try {
        await voice.speakOnce(TTS_PHRASES.speakerTest);
        // TTS が鳴った = トーン fallback は不要。以前の失敗で残っていれば止める。
        if (toneRef.current) stopTone();
      } catch {
        // TTS 合成/再生の失敗 → WebAudio の固定トーンへ fallback (D4)。生成が非同期な
        // ため、handle は必ず local に受けてから「既に完了済み / 既に生きているトーンが
        // ある」なら今作った方を捨てる (孤児化防止 — 止められない 440Hz が本編の VAD まで
        // 壊すのを防ぐ)。
        //
        // startTestTone() 自体が同期的に throw することがある (例: Chrome の同時
        // AudioContext 上限)。ここも try で包み announce() を reject させない — reject
        // させると呼び出し元 (runCycle) の .then() が実行されず、10 秒ごとの reprompt
        // 予約が二度と入らなくなる (ボタンは生きているので詰まりはしないが、以降ずっと
        // 無音のまま何も鳴らなくなる)。TTS と WebAudio が両方壊れている多重障害なので、
        // ここでは「このバーストは諦めて次の reprompt に賭ける」以上の作り込みはしない。
        try {
          const handle = startTestTone(outputDeviceIdRef.current ?? undefined);
          if (doneRef.current || toneRef.current) {
            handle.stop();
          } else {
            toneRef.current = handle;
          }
        } catch {
          // 固定トーンの生成にも失敗。今回のバーストは無音のまま諦める。
        }
        return; // 合成 (またはフォールバック) が壊れているので、このバーストは打ち切る
      }
    }
  };

  // 1 サイクル = 「鳴らす → 完了を待ってから約 10 秒後に次を予約」。予約は MAX_REPROMPTS 回
  // まで (D25-2)。自身を再帰参照するため戻り値型を明示する (無いと TS が循環参照で型を
  // 決められない)。
  //
  // レビュー修正 1: 10 秒タイマーは announce() の完了 (.then()) を待ってから起算する。
  // voice.speakOnce は再生完了 (ended) か stop() まで待つ Promise (ttsPlayer.ts) なので、
  // announce と同時に起算すると再生中に reprompt が発火し、play() が二重再生防止のため
  // 先に stop() する結果、バーストの途中でアナウンスが打ち切られて鳴り直す事故が起きる
  // (spec §3.1 の「2 回 → 10 秒待機 → 1 回」という順序どおりに直す)。
  //
  // 追い越しガード: clearTimer() だけでは「前の予約」しか消せない。再生ボタン (replay) で
  // runCycle が announce 完了前に再度呼ばれると、前の chain の announce はいずれ完了し
  // その .then() が独自にタイマーを仕掛けようとする — ガードが無いと二重の cadence が
  // 走る。gen を runCycle 呼び出しごとに採番し、.then() 内で「今も自分が最新世代か」を
  // 確認することで、追い越された chain のタイマー起算を無効化する。
  const runCycle = (plays: number): void => {
    cyclesRef.current += 1;
    setToneExhausted(false); // これから鳴らす
    clearTimer(); // 前サイクルの予約 (あれば) を破棄
    const gen = ++cycleGenRef.current;
    void announce(plays, gen).then(() => {
      // 追い越された chain を降ろすガード。直下の setTimeout コールバック内にも同じ
      // 条件のガードがあり、実質は二重化している (レビューの mutation でここだけ外しても
      // 全テストが赤くならないことを確認済み — stale chain がここを抜けても、後着の
      // setTimeout ガードで結局無効化されるため観測可能な差が出ない)。テストには
      // 守られていないが defense-in-depth として意図的に残す。「使われていないから」と
      // 削らないこと。
      if (doneRef.current || gen !== cycleGenRef.current) return;
      if (repromptsRef.current >= MAX_REPROMPTS) {
        // 打ち止め。空振りするタイマーを張らずに、鳴り終わった時点で表示を切り替える
        // (旧実装は 10 秒後に発火して cap に当たり何もせず return していた。その 10 秒は
        // 既に鳴らないことが確定しているのに「再生しています」と表示され続けていた、D26-4)。
        setToneExhausted(true);
        return;
      }
      timerRef.current = setTimeout(() => {
        timerRef.current = null;
        if (doneRef.current || gen !== cycleGenRef.current) return;
        repromptsRef.current += 1;
        runCycle(REPROMPT_PLAYS);
      }, REPROMPT_SILENCE_MS);
    });
  };

  useEffect(() => {
    doneRef.current = false;
    cyclesRef.current = 0;
    replaysRef.current = 0;
    repromptsRef.current = 0;
    runCycle(INITIAL_PLAYS);

    return () => {
      doneRef.current = true;
      clearTimer();
      stopTone();
      voice.cancel();
    };
    // voice は usePreflightVoice の返り値そのものが useMemo で安定しているため deps は
    // [voice] だけで正しい。runCycle/announce/stopTone/clearTimer は毎レンダー再生成される
    // 非メモ化ヘルパーだが、中で読むのは voice (deps 済み) と ref だけ。これらを deps に
    // 足すと逆に毎レンダーで effect が再発火し、cadence が丸ごと再起動してしまう
    // (NameCapture と同種の判断)。
    //
    // **不変条件: cadence は state を読まない (D26 で state を入れたため明文化)。**
    // reprompt タイマーは 10 秒後に runCycle を再帰呼び出しするので、予約した時点の
    // レンダーのクロージャを掴み続ける。ここから mode / toneExhausted を読むと必ず
    // stale になる。読んでよいのは voice と ref、書いてよいのは state setter だけ
    // (setter は React が同一性を保証する)。state に依存する挙動が必要になったら、
    // 毎レンダー同期する ref (modeRef 等) を足して ref 経由で読むこと。
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [voice]);

  // 3 つの再生ボタン (「聞こえないときは」/「🔊 もう一度鳴らす」/
  // 「🔊 この設定でもう一度鳴らす」) が共有する。初回と同じ 2 回を鳴らし直し、
  // 自動再生も復活させる (D25-3: 出力先を変えた直後は「初回」と同じ状況)。
  const replay = () => {
    if (doneRef.current) return;
    // D26-8: TTS 全滅で固定トーンに倒れていると、そのトーンは古い sink に貼り付いた
    // まま鳴り続ける (停止条件を持たない)。出力先を変えた検証を成立させるには現在の
    // sink で鳴らし直す必要がある。
    //
    // **ただし「止めるだけ」にしてはいけない** (レビュー指摘 F1)。次のトーンは announce
    // の catch でしか生まれず、そこへ到達するのは speakOnce が合成 timeout
    // (usePreflightVoice の 5 秒) で reject した後なので、押した瞬間から数秒まるごと
    // 無音になる — まさに音が聞こえない人が「押しても何も起きない」と読む区間を、
    // 変更前には無かった形で新設してしまう。新しい音を先に出してから古い音を止める
    // (張り替える) ことで、無音を挟まずに sink だけを移す。トーンが鳴っていなければ
    // (= TTS が生きている通常経路) 何もしない。
    const previous = toneRef.current;
    if (previous) {
      toneRef.current = null;
      try {
        toneRef.current = startTestTone(outputDeviceIdRef.current ?? undefined);
      } catch {
        // 生成に失敗 (例: Chrome の同時 AudioContext 上限)。toneRef は null のままなので、
        // 次のバーストの catch が作れればそこで現在の sink に張り直る。
      }
      previous.stop();
    }
    replaysRef.current += 1;
    repromptsRef.current = 0;
    runCycle(INITIAL_PLAYS);
  };

  const stats = (): SpeakerStats => ({
    playCycles: cyclesRef.current,
    replays: replaysRef.current,
  });

  const handleUnheard = () => {
    if (doneRef.current) return;
    setMode("troubleshooting");
    // emit は replay より先に行う (spec §8.1)。この 2 つの数は「諦めるまでにどれだけ
    // 待ったか」を表すので、押下自身が誘発する鳴らし直しを含めてはならない。
    onUnheardRef.current(stats());
    replay();
  };

  const handleHeardButton = () => {
    doneRef.current = true;
    clearTimer();
    stopTone();
    voice.cancel();
    onHeardRef.current("button", stats());
  };

  const canSelect = supportsOutputSelection();
  const selectableCount = countSelectableOutputs(devices);
  const advice = outputAdvice(canSelect, selectableCount);
  const troubleshooting = mode === "troubleshooting";

  return (
    <div data-testid="speaker-check" className="flex flex-col gap-4">
      {troubleshooting ? (
        <div className="bg-amber-50 border border-amber-300 rounded p-3 flex flex-col gap-3">
          <p className="text-[13px] font-semibold text-amber-800">
            音が届いていないようです。上から順にお試しください。
          </p>
          <div className="flex flex-col gap-2">
            <div className="grid grid-cols-[1.15rem_1fr] gap-x-1.5">
              <span className="text-[13px] font-bold text-amber-800">1</span>
              <p className="text-[13px] text-amber-800">音量を上げ、ミュートを解除する</p>
              <p className="col-start-2 text-[11px] text-amber-700">
                端末の音量、ヘッドホンのつまみ、ブラウザのタブのミュート (🔇)
              </p>
            </div>
            <div className="grid grid-cols-[1.15rem_1fr] gap-x-1.5">
              <span className="text-[13px] font-bold text-amber-800">2</span>
              <p className="text-[13px] text-amber-800">{advice.title}</p>
              <p className="col-start-2 text-[11px] text-amber-700">{advice.sub}</p>
            </div>
          </div>
        </div>
      ) : (
        <p className="text-sm text-slate-700">
          {toneExhausted
            ? "テスト音声の自動再生は終わりました。聞こえた場合は「聞こえました」を押してください。"
            : "テスト音声を再生しています。聞こえましたら「聞こえました」を押してください。"}
        </p>
      )}
      {canSelect ? (
        <DeviceSelect
          label="スピーカー"
          devices={devices}
          selectedId={outputDeviceId}
          onSelect={onSelectOutput}
          /* D26-5: 対処モードでは救済手段であることを見た目でも示す。表示条件そのものは
             変えない — モード切替で DOM が動くと、先にプルダウンを触っていた人が迷子になる */
          highlighted={troubleshooting && selectableCount > 1}
        />
      ) : (
        <p className="text-xs bg-blue-50 border border-blue-300 text-blue-800 rounded p-3">
          このブラウザでは出力先を選べません。OS のサウンド設定で出力先を選んでください。
        </p>
      )}
      {/* 以下のボタンは min-h-[44px] + inline-flex で Apple HIG のタップ領域 44pt を確保する
          (実測の高さは 36px だった)。見た目の px / py / 色 / 角丸と文言は据え置く */}
      {troubleshooting ? (
        <>
          <div className="flex gap-2 justify-center">
            <button
              type="button"
              onClick={replay}
              className="min-h-[44px] inline-flex items-center justify-center px-6 py-2 rounded-full text-sm font-semibold bg-indigo-600 text-white hover:bg-indigo-700"
            >
              🔊 この設定でもう一度鳴らす
            </button>
          </div>
          <div className="flex items-center gap-2 text-[11px] text-slate-500">
            <span className="flex-1 h-px bg-slate-200" />
            聞こえたら
            <span className="flex-1 h-px bg-slate-200" />
          </div>
          <div className="flex justify-center">
            {/* まだ聞けていないのに押せる状態を主動線にしない。今やることは再テスト */}
            <button
              type="button"
              onClick={handleHeardButton}
              className="min-h-[44px] inline-flex items-center justify-center px-6 py-2 rounded-full text-sm font-semibold bg-white border border-indigo-600 text-indigo-700 hover:bg-indigo-50"
            >
              聞こえました
            </button>
          </div>
        </>
      ) : (
        <>
          <div className="flex gap-2 justify-center">
            <button
              type="button"
              onClick={handleHeardButton}
              className="min-h-[44px] inline-flex items-center justify-center px-6 py-2 rounded-full text-sm font-semibold bg-indigo-600 text-white hover:bg-indigo-700"
            >
              聞こえました
            </button>
            <button
              type="button"
              onClick={handleUnheard}
              className="min-h-[44px] inline-flex items-center justify-center px-4 py-2 rounded-full text-sm font-semibold bg-slate-100 text-slate-700 hover:bg-slate-200"
            >
              聞こえないときは
            </button>
          </div>
          {/* D26-3: 常時表示。条件表示にすると、鳴らし直したいだけの人が申告ボタンを
              押すことになり preflight.speaker_unheard が汚れる。また speakOnce が
              settle しない経路では、これが唯一の復旧手段になる。
              文字リンク型なので px-3 + min-h-[44px] でタップ領域を 44pt に広げる。
              文字は 13px (HIG の Footnote 相当) — 11px は HIG の最小 (Caption 2) だが
              あれはキャプションや注釈の下限で、ボタンのラベルに使う値ではない
              (Material 3 のボタンラベルは labelLarge = 14sp)。Pixel 7a の実機で
              「小さすぎる」と報告されたため 11px から上げた。主動線の CTA との差は
              塗りつぶしの有無・文字色・太さが担うので、サイズで下げる必要はない */}
          <button
            type="button"
            onClick={replay}
            className="self-center inline-flex items-center min-h-[44px] px-3 text-[13px] text-indigo-600 hover:text-indigo-800 hover:underline"
          >
            🔊 もう一度鳴らす
          </button>
        </>
      )}
    </div>
  );
}
